import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import AccessorialRevenueForecast from "./forecast/accessorialRevenueForecast";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import moment from "moment";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { AccessorialRevenueDailyTrendState, AccessorialRevenueTrendIndicatorsState } from "slices/shipment-reconciliation/ShipmentReconciliationState";
import { accessorialRevenueDailyTrendSelector, getAccessorialRevenueDailyTrend } from "slices/shipment-reconciliation/revenue-insights/accessorialRevenueDailyTrendSlice";
import { toast } from "react-toastify";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { terminalOptions } from "app/data/shipment-reconciliation/models";
import Table from "ui-components/table/table";
import { getAccessorialsRevenueDailyTrendColumns } from "./accessorialsRevenueDailyTrendColumns";
import { accessorialRevenueTrendIndicatorsSelector, getAccessorialRevenueTrendIndicators } from "slices/shipment-reconciliation/revenue-insights/accessorialRevenueTrendIndicatorsSlice";
import { RevenueInsightsSummary } from "./summary/revenueInsightsSummary";
import AccessorialReviewTrend from "./trends/accessorialReviewTrend";
import Switch from "react-switch";
import "./accessorialRevenueInsights.scss";

const AccessorialRevenueInsights: React.FC<{}> = memo(() => {
  const dispatch = useDispatch();
  const [dateFrom, setDateFrom] = useState<string>(moment().subtract(2, "weeks").toString());
  const [dateTo, setDateTo] = useState<string>(moment().toString());
  const [terminal, setTerminal] = useState<XGSSelectOption | null | undefined>();
  const [isShowSummaryMetrics, setIsShowSummaryMetrics] = useState<boolean>(true);
  const [isShowSystemMetrics, setIsShowSystemMetrics] = useState<boolean>(false);
  const [isShowOperationsMetrics, setIsShowOperationsMetrics] = useState<boolean>(false);
  const [isShowBillingMetrics, setIsShowBillingMetrics] = useState<boolean>(false);

  const accessorialRevenueDailyTrendState: AccessorialRevenueDailyTrendState = useSelector(
    accessorialRevenueDailyTrendSelector
  );

  const accessorialRevenueTrendIndicatorsState: AccessorialRevenueTrendIndicatorsState = useSelector(
    accessorialRevenueTrendIndicatorsSelector
  );

  useEffect(() => {
    dispatch(getAccessorialRevenueTrendIndicators(
      {
        startDateAdded: (dateFrom) ? dateFrom.toApiDateFormat() : "",
        endDateAdded: (dateTo) ? dateTo.toApiDateFormat() : "",
        terminalNumber: (terminal && terminal.value) ? +terminal.value : null
      },
      (error) => { toast.error(error) }
    ));
    dispatch(getAccessorialRevenueDailyTrend(
      {
        startDateAdded: (dateFrom) ? dateFrom.toApiDateFormat() : "",
        endDateAdded: (dateTo) ? dateTo.toApiDateFormat() : "",
        terminalNumber: (terminal && terminal.value) ? +terminal.value : null
      },
      (error) => { toast.error(error) }
    ));
  }, [dispatch, dateFrom, dateTo, terminal])

  const onTerminalChanged = (_destination: XGSSelectOption | null | undefined) => {
    setTerminal(_destination);
  };


  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ 'full-width': true }}
      titleComponent={
        <ContentContainerToolbar
          title="Accessorial Revenue Insights"
          mods={{ "full-width": true }}
        >
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-accessorial-revenue-insights">
        <div className="xgs-accessorial-revenue-insights__controls">
          <div className="xgs-accessorial-revenue-insights__controls__search">
            <LabeledDateRangeInput
              label="Date Added"
              labelMode={LabelModes.column}
              className="xgs-accessorial-revenue-insights__controls__search__input"
              start={dateFrom}
              end={dateTo}
              onStartChange={(v) => {
                setDateFrom(v);
              }}
              onEndChange={(v) => {
                setDateTo(v);
              }}
            />
            <div className="xgs-accessorial-revenue-insights__controls__search__input">
              <LabeledSelectInput
                onValueChange={onTerminalChanged}
                value={terminal}
                options={terminalOptions}
                label="Terminal:"
                labelMode={LabelModes.column}
                className="xgs-tracking-filter__input"
                isClearable
              />
            </div>
          </div>
          <div className="xgs-accessorial-revenue-insights__controls__toggles">
            <div className="xgs-accessorial-revenue-insights__controls__toggles__heading">
              Show Metrics For:
            </div>
            <div className="xgs-accessorial-revenue-insights__controls__toggles__field">
              Summary:
              <Switch
                onChange={(checked) => setIsShowSummaryMetrics(checked)}
                checked={isShowSummaryMetrics}
                disabled={false}
                width={32}
                height={20}
                offColor="#BDBDBD"
                onColor="#2F80ED"
                uncheckedIcon={<></>}
                checkedIcon={<></>}
              />
            </div>
            <div className="xgs-accessorial-revenue-insights__controls__toggles__field">
              System:
              <Switch
                onChange={(checked) => setIsShowSystemMetrics(checked)}
                checked={isShowSystemMetrics}
                disabled={false}
                width={32}
                height={20}
                offColor="#BDBDBD"
                onColor="#2F80ED"
                uncheckedIcon={<></>}
                checkedIcon={<></>}
              />
            </div>
            <div className="xgs-accessorial-revenue-insights__controls__toggles__field">
              Operations:
              <Switch
                onChange={(checked) => setIsShowOperationsMetrics(checked)}
                checked={isShowOperationsMetrics}
                disabled={false}
                width={32}
                height={20}
                offColor="#BDBDBD"
                onColor="#2F80ED"
                uncheckedIcon={<></>}
                checkedIcon={<></>}
              />
            </div>
            <div className="xgs-accessorial-revenue-insights__controls__toggles__field">
              Billing:
              <Switch
                onChange={(checked) => setIsShowBillingMetrics(checked)}
                checked={isShowBillingMetrics}
                disabled={false}
                width={32}
                height={20}
                offColor="#BDBDBD"
                onColor="#2F80ED"
                uncheckedIcon={<></>}
                checkedIcon={<></>}
              />
            </div>
          </div>
        </div>
        <RevenueInsightsSummary
          summaryData={accessorialRevenueTrendIndicatorsState.indicators}
          isLoading={accessorialRevenueTrendIndicatorsState.loading}
          isShowSummaryMetrics={isShowSummaryMetrics}
          isShowSystemMetrics={isShowSystemMetrics}
          isShowOperationsMetrics={isShowOperationsMetrics}
          isShowBillingMetrics={isShowBillingMetrics}
        />
        <div className="xgs-accessorial-revenue-insights__table-wrapper">
          <Table
            isLoading={accessorialRevenueDailyTrendState.loading}
            columns={getAccessorialsRevenueDailyTrendColumns()}
            data={accessorialRevenueDailyTrendState.results}
            ignoreCellClick="selection"
            cursorPointer={true}
            noResultsText="No data available"
            onSelectedRowsChange={null}
            infiniteScroll={true}
            responsive={true}
            sorting={true}
            enableMultiSort={true}
            minTableHeight={420}
            autoResetSelectedRows={true}
            keepTableOnLoading={true}
          />
        </div>
        <AccessorialRevenueForecast
          isLoading={accessorialRevenueTrendIndicatorsState.loading}
          averageDeliveriesPerDay={accessorialRevenueTrendIndicatorsState.indicators.averageDalyDeliveries}
          averageAccessorialRevenue={accessorialRevenueTrendIndicatorsState.indicators.avaerageRevenuePerAccessorial}
          findingsRate={accessorialRevenueTrendIndicatorsState.indicators.accessorialFindingsRate}
          operationsApprovalRate={accessorialRevenueTrendIndicatorsState.indicators.operationsApprovalRate}
          billingApprovalRate={accessorialRevenueTrendIndicatorsState.indicators.billingApprovalRate}
          averageDailyScans={accessorialRevenueTrendIndicatorsState.indicators.averageDailyScans}
        />
        <div className="xgs-accessorial-revenue-insights__trends">
          <div className="xgs-accessorial-revenue-insights__trends__title">
            Trends
          </div>
          <AccessorialReviewTrend
            accessorialsPerformance={(accessorialRevenueDailyTrendState.results && accessorialRevenueDailyTrendState.results.length) ? [...accessorialRevenueDailyTrendState.results].sort((a: any, b: any) => a.dateAdded.localeCompare(b.dateAdded)) : []}
          />
        </div>
      </div>
    </ContentContainer >
  );
});

export default AccessorialRevenueInsights;
